/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hsa_heart_rate_data` message.
 *
 * User's current heart rate data used for HSA custom data logging
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HsaHeartRateDataMesg : Mesg {
    public constructor() : super("HsaHeartRateData", Profile.MesgNum.HSA_HEART_RATE_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** processing_interval, in s. Processing interval length in seconds */
    public var processingInterval: UShort?
        get() = getFieldValue(PROCESSING_INTERVAL_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PROCESSING_INTERVAL_FIELD_NUM, value)

    /** status. Status of measurements in buffer - 0 indicates SEARCHING 1 indicates LOCKED */
    public var status: UByte?
        get() = getFieldValue(STATUS_FIELD_NUM) as? UByte
        set(value) = setFieldValue(STATUS_FIELD_NUM, value)

    /** Number of values stored in heart_rate. */
    public val numHeartRate: Int
        get() = getField(HEART_RATE_FIELD_NUM)?.numValues ?: 0

    /** heart_rate, in bpm. Beats / min. Blank: 0 */
    public var heartRate: List<UByte?>?
        get() {
            val field = getField(HEART_RATE_FIELD_NUM) ?: return null
            return List(field.numValues) { getHeartRate(it) }
        }
        set(value) {
            removeField(HEART_RATE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setHeartRate(index, element) }
        }

    public fun getHeartRate(index: Int): UByte? =
        getFieldValue(HEART_RATE_FIELD_NUM, index) as? UByte

    public fun setHeartRate(index: Int, value: UByte?) {
        setFieldValue(HEART_RATE_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val PROCESSING_INTERVAL_FIELD_NUM: UByte = 0u
        public const val STATUS_FIELD_NUM: UByte = 1u
        public const val HEART_RATE_FIELD_NUM: UByte = 2u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("ProcessingInterval", 0u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[1] = Field("Status", 1u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[2] = Field("HeartRate", 2u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            return fields
        }
    }
}
